import * as React from "react"

import { Override } from "../../data/WithOverride"

import { JSONObject } from "../types/JSONData"
import { PropertyControls } from "../types/PropertyControls"

/**
 * @internal
 * TODO: delete this type when all the framer runtime logic is extracted to Source/Runtime
 */
export type ComponentIdentifier = string

/**
 * @internal
 * TODO: delete this type when all the framer runtime logic is extracted to Source/Runtime
 */
export type ComponentMap = {
    [name: string]: ComponentDefinition
}

/**
 * @internal
 * TODO: delete this type when all the framer runtime logic is extracted to Source/Runtime
 */
export type ErrorDefinition = ComponentDefinition<{}> & {
    error: Error | string

    fileDoesNotExist?: boolean
}

/**
 * @internal
 */
export interface ComponentLoader {
    /**
     * @internal
     */
    packageDisplayName(packageIdentifier: PackageIdentifier): string | undefined
    /**
     * @internal
     */
    localPackageIdentifier(): PackageIdentifier
    /**
     * Names of the packages that are direct dependencies of the project.
     * @internal
     */
    packageIdentifiers(): PackageIdentifier[]
    /**
     * @internal
     */
    componentsForPackage(packageIdentifier: PackageIdentifier): ComponentDefinition[]
    /**
     * @internal
     */
    componentForIdentifier(identifier: ComponentIdentifier): ComponentDefinition | null
    /**
     * @internal
     */
    errorForIdentifier(identifier: ComponentIdentifier): ErrorDefinition | null
    /**
     * Identifiers of the components that are in the current project
     * or in packages that are direct dependencies of the project.
     * @internal
     */
    componentIdentifiers(): ComponentIdentifier[]
    /**
     * @internal
     */
    forEachDesignComponents(cb: (component: DesignComponentDefinition) => boolean | void): void
    /**
     * @internal
     */
    forEachComponent(cb: (component: ComponentDefinition) => boolean | void): void
    /**
     * @internal
     */
    tokensForPackage(packageIdentifier: PackageIdentifier): TokenMap | undefined
    /**
     * @internal
     */
    packageFileNames(packageIdentifier: PackageIdentifier): string[]
}

/**
 * NOTE: Also defined as ComponentType in the Server project.
 * @internal
 * TODO: delete this type when all the framer runtime logic is extracted to Source/Runtime
 */
export type ComponentType = "component" | "device" | "deviceHand" | "deviceSkin" | "master" | "override"

/**
 * @internal
 * TODO: delete this type when all the framer runtime logic is extracted to Source/Runtime
 */
export type PackageIdentifier = string

/**
 * @internal
 * TODO: delete this type when all the framer runtime logic is extracted to Source/Runtime
 */
export type ComponentDefinition<P = any> = {
    class: React.ComponentType<P> | JSON | Override<any>
    /** Package depth of this component. 0 if part of project, 1 if a direct dependency, greater otherwise. */
    depth: number
    file: string
    identifier: ComponentIdentifier
    name: string
    /** Identifier of the package that contains this component (one package can contain multiple components). */
    packageIdentifier: PackageIdentifier
    properties: PropertyControls<P>
    type: ComponentType

    defaults?: P
}

/**
 * @internal
 */
export type ReactComponentDefinition<P = any> = ComponentDefinition<P> & { class: React.ComponentType<P> }

/**
 * @internal
 */
export type DesignComponentDefinition = ComponentDefinition & { class: JSONObject }

/**
 * @internal
 */
export type TokenIdentifier = string

/**
 * @internal
 */
export interface TokenDefinition {
    __class: string
    id: TokenIdentifier
    name: string
    value: string
}

/**
 * @internal
 */
export type TokenMap = { [key: string]: TokenDefinition }

/**
 * @internal
 */
export function isDesignDefinition(d: ComponentDefinition): d is DesignComponentDefinition {
    return d.type === "master"
}

/**
 * @internal
 */
export function isNonUserFacing(d: ComponentDefinition): boolean {
    return d.type === "device" || d.type === "deviceSkin" || d.type === "deviceHand"
}

/**
 * @internal
 */
export function isOverride(d: ComponentDefinition): boolean {
    return d.type === "override"
}

/**
 * @internal
 */
export function isReactDefinition<P = any>(d: ComponentDefinition<P>): d is ReactComponentDefinition<P> {
    return d.type !== "master"
}
